const sharedNoiseMessagePatterns = [
  /Invalid call to runtime\.sendMessage\(\)\. Tab not found\./i,
  /feature named `hover` was not found/i,
  /feature named `pageObserver` was not found/i,
  /Encountered %o when trying/i,
  /Load config error: %O SyntaxError: Unexpected end of JSON input/i,
  /\[Termly\] error fetching configuration/i,
  /UnhandledRejection: Non-Error promise rejection captured with value: Object Not Found Matching Id/i,
  /Prettify fetch failed:/i,
  /SyntaxError: The string did not match the expected pattern\./i,
  /Event `[^`]+` \(type=[^)]+\) captured as promise rejection/i,
  /(?:Non-Error|Object) captured as promise rejection with keys: message/i,
  /Navigation (cancelled|aborted|redirected) from "[^"]+" to "[^"]+"/i,
  /^Unauthorized$/,
  /^Token has been invalidated$/,
  /^Network Error$/,
  // Same benign axios transport case as ^Network Error$ above: the browser aborts an in-flight
  // XHR on navigation/page-unload, and handleErrorResponse re-wraps it as a plain Error('Request
  // aborted'), stripping the AxiosError type the other axios filters key off. Anchored so it only
  // matches that exact re-wrapped value.
  /^Request aborted$/,
  // Chrome's Trusted Types policy refusing a call from a page that is being torn down: Vue's innerHTML patch
  // goes through it, and the headless link scanners take the page apart mid-render. Every one of these is
  // Chrome on Linux (sc-28743); see shouldDropBodylessDocumentNoise for the same scanners' other errors.
  /The provided callback is no longer runnable/i
]

const browserExtensionFramePatterns = [
  /^(user-script|chrome-extension|moz-extension|safari-web-extension|safari-extension):/i,
  /extension:\/\//i,
  // Not an extension, but the same thing to us: Facebook's and Instagram's in-app browsers inject their own
  // scripts under this scheme, and those throw "Java object is gone" as the page unloads (sc-28743).
  /^iabjs:/i
]

// Vue transition `after-leave` callbacks can fire while the document is being torn down
// (user navigates away mid-transition), at which point `document.body` is null and any
// `body.classList` access in a Quasar plugin throws. Pure unload-race noise — drop it.
const afterLeaveClassListMessagePattern = /Cannot read properties of null \(reading 'classList'\)/i
const afterLeaveStackPattern = /\bat after-leave\b/i

// Quasar's Loading plugin appends its spinner to document.body from a setTimeout (deferred
// even at delay 0). Automated headless-Chrome clients (link/preview scanners opening emailed
// public links) tear the document down before that timer fires, so document.body is null and
// appendChild throws. Confirmed noise - real users on the same pages never trigger it.
const quasarLoadingBodyNullMessagePattern = /Cannot read properties of null \(reading 'appendChild'\)/i
const quasarLoadingFramePattern = /quasar\/src\/plugins\/Loading/i

// Neither filter above matches Vue 3 and Quasar 2: the stack now reads `onAfterLeave`, and beforeSend only
// ever sees the minified bundle, so frame paths like `quasar/src/plugins/Loading` are not in it. See
// `shouldDropBodylessDocumentNoise`, which does not depend on either.

function getSentryErrors (event: any) {
  return event.exception?.values || []
}

function getSentryFrames (event: any) {
  return getSentryErrors(event).reduce((frames: any[], error: any) => {
    return frames.concat(error.stacktrace?.frames || [])
  }, [])
}

// captureConsoleIntegration stores the original Error under event.extra.arguments.
// That Error carries its real stack as a string (not parsed into frames), which
// is where extension/userscript markers like `user-script:` end up. Surface them
// so frame-filename pattern checks can still match.
function getSentryExtraStackStrings (event: any): string[] {
  const args = event.extra?.arguments
  if (!Array.isArray(args)) {
    return []
  }

  const stacks: string[] = []
  for (const arg of args) {
    if (arg && typeof arg === 'object' && typeof arg.stack === 'string') {
      stacks.push(arg.stack)
    }
  }
  return stacks
}

export function getSentryEventMessages (event: any) {
  const messages = new Set<string>()

  if (event.message) {
    messages.add(event.message)
  }

  if (event.logentry?.message) {
    messages.add(event.logentry.message)
  }

  if (event.logentry?.formatted) {
    messages.add(event.logentry.formatted)
  }

  getSentryErrors(event).forEach((error: any) => {
    if (error.value) {
      messages.add(error.value)
    }

    if (error.type && error.value) {
      messages.add(`${error.type}: ${error.value}`)
    }
  })

  return [...messages]
}

export function shouldDropSharedSentryNoise (event: any) {
  const eventMessages = getSentryEventMessages(event)

  return eventMessages.some((message) => sharedNoiseMessagePatterns.some((pattern) => pattern.test(message)))
}

export function shouldDropAfterLeaveTransitionNoise (event: any) {
  const eventMessages = getSentryEventMessages(event)

  if (!eventMessages.some((message) => afterLeaveClassListMessagePattern.test(message))) {
    return false
  }

  return getSentryExtraStackStrings(event).some((stack) => afterLeaveStackPattern.test(stack))
}

export function shouldDropQuasarLoadingBodyNullNoise (event: any) {
  const eventMessages = getSentryEventMessages(event)

  if (!eventMessages.some((message) => quasarLoadingBodyNullMessagePattern.test(message))) {
    return false
  }

  return getSentryFrames(event).some((frame: any) =>
    typeof frame.filename === 'string' && quasarLoadingFramePattern.test(frame.filename)
  )
}

/**
 * The page has lost its `<body>`. Headless link scanners (every one of these events is Chrome on Linux)
 * take the document apart while Vue transitions and Quasar plugins still have work queued, and each
 * `document.body.x` read in that work throws: `offsetHeight` in Vue's forceReflow, `classList` in Quasar's
 * prevent-scroll, `style` in ShowMessageDialog (sc-28743). Nobody is looking at a page with no body.
 *
 * Asked of the document rather than of the event: the messages are Vue 3's now, and the frames are minified.
 */
export function shouldDropBodylessDocumentNoise () {
  return typeof document !== 'undefined' && document.body === null
}

export function shouldDropBrowserExtensionNoise (event: any) {
  const hasExtensionFrame = getSentryFrames(event).some((frame: any) => {
    const filename = frame.filename

    return typeof filename === 'string' && browserExtensionFramePatterns.some((pattern) => pattern.test(filename))
  })

  if (hasExtensionFrame) {
    return true
  }

  // Errors captured via captureConsoleIntegration have no parsed frames; their real
  // stack string lives in event.extra.arguments[*].stack. Scan those too so injected
  // userscript/extension noise is dropped regardless of capture path.
  return getSentryExtraStackStrings(event).some((stack) =>
    browserExtensionFramePatterns.some((pattern) => pattern.test(stack))
  )
}

export function applySentryNoiseCandidateTags (event: any) {
  const errors = getSentryErrors(event)
  const eventMessages = getSentryEventMessages(event)
  const hasStackFrames = getSentryFrames(event).length > 0
  const hasBooksproutRequest = /(booksprout\.co|localhost)/i.test(`${event.request?.url || event.culprit || ''}`)
  const hasUnhandledRejection = errors.some((error: any) => error.mechanism?.type === 'onunhandledrejection')
  const hasAxiosSignature = eventMessages.some((message) => /^AxiosError:/i.test(message))

  if (event.tags?.noise_candidate) {
    return event
  }

  if (event.tags?.third_party_code) {
    event.tags = {
      ...event.tags,
      noise_candidate: 'third-party-stack'
    }

    return event
  }

  if (!hasStackFrames && !event.crashFile && hasUnhandledRejection && hasBooksproutRequest && !hasAxiosSignature) {
    event.tags = {
      ...event.tags,
      noise_candidate: 'stackless-unhandled-rejection'
    }
  }

  return event
}
